import { useCallback } from 'react';
import {
  MAX_IMPORT_TEXT_LENGTH,
  extractTextFromImportFile,
} from '../../components/Dashboard/ImportLogs';
import {
  buildNaturalCapturePipelineSnapshot,
  detectNaturalCaptureInputType,
  traceNaturalCapturePipeline,
} from '../../components/Dashboard/ImportLogs/importLogsPipeline';
import {
  CARE_SOURCE_TYPES,
  captureCareSourceForProcessing,
  saveFileCareSourceForProcessing,
  saveImageCareSourceFileForProcessing,
  uploadOriginalFileToStorage,
} from '../../features/careSourceProcessing';
import {
  CARE_SOURCE_TEXT_EXTRACTION_STATUSES,
} from '../../features/careSourceProcessing/careSourceMetadata';
import { isImageFile } from '../../features/careSourceProcessing/textExtraction/careSourceFileTextExtraction';
import { createImportHistoryBatch, updateImportHistoryBatchItem } from '../../features/importHistory/importHistoryBatchService';
import { MAX_IMPORT_HISTORY_FILES } from '../../features/importHistory/importHistoryBatchModel';

const DEFAULT_CONTROLLER_DEPS = {};
const MAX_IMPORT_HISTORY_SOURCE_TEXT_LENGTH = 90000;

const buildImportFileControllerDeps = (deps = {}) => ({
  extractTextFromImportFile,
  captureCareSourceForProcessing,
  saveFileCareSourceForProcessing,
  saveImageCareSourceFileForProcessing,
  uploadOriginalFileToStorage,
  createImportHistoryBatch,
  updateImportHistoryBatchItem,
  ...deps,
});

const resolveFileImportErrorMessage = (error) => {
  const rawMessage = String(error?.message || '').trim();
  const normalizedMessage = rawMessage.toLowerCase();
  const isInternalCallableFailure =
    error?.code === 'internal' ||
    error?.code === 'deadline-exceeded' ||
    normalizedMessage === 'internal' ||
    normalizedMessage.includes('504') ||
    normalizedMessage.includes('gateway timeout') ||
    normalizedMessage.includes('failed to load resource') ||
    normalizedMessage.includes('net::err_failed');

  return isInternalCallableFailure
    ? 'That file took too long to import. Please split it into a smaller file or try again with fewer rows.'
    : (rawMessage || "We couldn't read that file. Try a simpler format.");
};

export const useImportFileReviewController = ({
  children = [],
  currentChildId = '',
  closeImportStartModal,
  startImportLoading,
  stopImportLoading,
  clearImportError,
  setImportError,
  setImportStatus = null,
  selectedSourceType = CARE_SOURCE_TYPES.FILE_IMPORT,
  selectedDate = null,
  selectedLocalDate = null,
  sourceUseCase = '',
  reviewPolicy = '',
  user = null,
  importBatch = null,
  deps = DEFAULT_CONTROLLER_DEPS,
} = {}) => {
  const handleImportFileChange = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (!files.length) {
      return;
    }
    if (files.length > MAX_IMPORT_HISTORY_FILES) {
      setImportError(`Choose no more than ${MAX_IMPORT_HISTORY_FILES} files at a time.`);
      return;
    }

    closeImportStartModal();

    try {
      const controllerDeps = buildImportFileControllerDeps(deps);
      startImportLoading({
        title: 'Saving your source',
        subtitle: "We'll keep the original and organize care details in the background.",
      });
      clearImportError();
      setImportStatus?.('');

      const selectedChild = children.find((child) => child.id === currentChildId)
        || children[0]
        || (currentChildId ? { id: currentChildId } : null);
      if (!selectedChild?.id) {
        throw new Error('Please select a child before importing logs.');
      }

      const isBatchImport = sourceUseCase === 'import_history';
      const batch = isBatchImport
        ? (importBatch || await controllerDeps.createImportHistoryBatch({ childId: selectedChild.id, userId: user?.uid || '', files }))
        : null;
      const batchItems = batch ? [...batch.items] : [];
      const claimedRetryIndexes = new Set();
      const findRetryItemIndex = (file) => batchItems.findIndex((item, index) => (
        item.uploadStatus === 'failed'
        && !claimedRetryIndexes.has(index)
        && item.fileName === file.name
        && Number(item.fileSize || 0) === Number(file.size || 0)
        && (!item.fileType || !file.type || item.fileType === file.type)
      ));

      for (let fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
        const file = files[fileIndex];
        const retryItemIndex = importBatch ? findRetryItemIndex(file) : -1;
        if (importBatch && retryItemIndex < 0) {
          throw new Error(`Choose the same failed file again: ${file.name}`);
        }
        const batchItemIndex = importBatch ? retryItemIndex : fileIndex;
        claimedRetryIndexes.add(batchItemIndex);
        const fileExtension = String(file?.name || '').toLowerCase().split('.').pop();
        const fileMimeType = String(file?.type || '').trim().toLowerCase();
        try {
          let sourceResult = null;
          if (isImageFile(fileExtension, fileMimeType)) {
            sourceResult = await controllerDeps.saveImageCareSourceFileForProcessing({
          childId: selectedChild.id,
          file,
          sourceType: selectedSourceType,
          selectedDate,
          selectedLocalDate,
          sourceUseCase,
          reviewPolicy,
          importBatchId: batch?.id || '',
          importBatchOrder: batchItemIndex,
          deps: {
            captureCareSourceForProcessing: controllerDeps.captureCareSourceForProcessing,
          },
            });
            traceNaturalCapturePipeline('import-source-saved-for-background-processing', buildNaturalCapturePipelineSnapshot({
          stage: 'import-source-saved-for-background-processing',
          inputType: detectNaturalCaptureInputType({
            source: 'import',
            isImported: true,
            isLongNote: false,
          }),
          text: '',
          metadata: {
            sourceNoteId: sourceResult?.sourceNoteId || '',
            sourceType: selectedSourceType,
            fileName: file?.name || '',
            fileType: file?.type || '',
            fileSize: file?.size || 0,
          },
            }));
            setImportStatus?.("Source saved. We're organizing care details in the background.");
          } else {
            const originalFileAttachment = await controllerDeps.uploadOriginalFileToStorage({
          childId: selectedChild.id,
          file,
        });
        let extractedText = '';
        let textExtractionStatus = CARE_SOURCE_TEXT_EXTRACTION_STATUSES.EXTRACTED;
        let extractionError = null;
        try {
          extractedText = await controllerDeps.extractTextFromImportFile(file);
        } catch (error) {
          textExtractionStatus = CARE_SOURCE_TEXT_EXTRACTION_STATUSES.FAILED;
          extractionError = {
            code: 'text-extraction-failed',
            message: error?.message || 'We could not read text from this file yet.',
          };
        }
        if (isBatchImport && extractedText.length > MAX_IMPORT_HISTORY_SOURCE_TEXT_LENGTH) {
          extractedText = '';
          textExtractionStatus = CARE_SOURCE_TEXT_EXTRACTION_STATUSES.FAILED;
          extractionError = {
            code: 'extracted-text-too-large',
            message: 'The original is preserved, but its extracted text is too large to organize. Split the document and retry if you need entries extracted.',
          };
        }
        traceNaturalCapturePipeline('import-extracted-text', buildNaturalCapturePipelineSnapshot({
          stage: 'import-extracted-text',
          inputType: detectNaturalCaptureInputType({
            source: 'import',
            isImported: true,
            isLongNote: false,
          }),
          text: extractedText,
          metadata: {
            fileName: file?.name || '',
            fileType: file?.type || '',
            fileSize: file?.size || 0,
          },
        }));

        if (!isBatchImport && extractedText.length > MAX_IMPORT_TEXT_LENGTH) {
          throw new Error('That file is too large to import. Please split it into a smaller file and try again.');
        }

            sourceResult = await controllerDeps.saveFileCareSourceForProcessing({
          childId: selectedChild.id,
          rawText: extractedText,
          sourceType: selectedSourceType,
          file,
          originalFileAttachment,
          textExtractionStatus,
          extractionError,
          selectedDate,
          selectedLocalDate,
          sourceUseCase,
          reviewPolicy,
          importBatchId: batch?.id || '',
          importBatchOrder: batchItemIndex,
          deps: {
            captureCareSourceForProcessing: controllerDeps.captureCareSourceForProcessing,
          },
            });

        traceNaturalCapturePipeline('import-source-saved-for-background-processing', buildNaturalCapturePipelineSnapshot({
          stage: 'import-source-saved-for-background-processing',
          inputType: detectNaturalCaptureInputType({
            source: 'import',
            isImported: true,
            isLongNote: false,
          }),
          text: extractedText,
          metadata: {
            sourceNoteId: sourceResult?.sourceNoteId || '',
            sourceType: selectedSourceType,
            fileName: file?.name || '',
            fileType: file?.type || '',
            fileSize: file?.size || 0,
          },
        }));
            setImportStatus?.("Source saved. We're organizing care details in the background.");
          }
          if (batch) {
            batchItems[batchItemIndex] = { ...batchItems[batchItemIndex], fileName: file.name, fileType: file.type || '', fileSize: Number(file.size || 0), uploadStatus: 'saved', sourceNoteId: sourceResult?.sourceNoteId || '', error: null };
            await controllerDeps.updateImportHistoryBatchItem(batch.id, batchItems[batchItemIndex].key, batchItems[batchItemIndex]);
          }
        } catch (fileError) {
          if (!batch) throw fileError;
          batchItems[batchItemIndex] = { ...batchItems[batchItemIndex], uploadStatus: 'failed', error: { message: resolveFileImportErrorMessage(fileError) } };
          await controllerDeps.updateImportHistoryBatchItem(batch.id, batchItems[batchItemIndex].key, batchItems[batchItemIndex]);
        }
      }
      if (batch) {
        setImportStatus?.('Import batch saved. Processing will continue in the background.');
      }
    } catch (error) {
      setImportStatus?.('');
      setImportError(resolveFileImportErrorMessage(error));
    } finally {
      stopImportLoading();
    }
  }, [
    children,
    clearImportError,
    closeImportStartModal,
    currentChildId,
    deps,
    selectedSourceType,
    selectedDate,
    selectedLocalDate,
    sourceUseCase,
    reviewPolicy,
    user,
    importBatch,
    setImportError,
    setImportStatus,
    startImportLoading,
    stopImportLoading,
  ]);

  return {
    handleImportFileChange,
  };
};

export default useImportFileReviewController;
